import React, { FunctionComponent, useState } from 'react'
import { withFloorMap } from '@wework/react-floormap'
import {
  Button,
  Form,
  Modal,
  Grid,
  Text,
  Input,
  Dimmer,
} from '@wework/dieter-ui'
import IMTheme from '../../style/IMTheme'
import { CheckIcon } from '../Icons'
import DragNDroopFile from '../Common/DragNDropFile'
import { Loader } from 'semantic-ui-react'
import { withSnackbar } from '../../hoc/SnackBarProvider'
import compose from '../../utils/compose'

interface EditSpaceLoadImageModalProps {
  floorMap: any
  snackBars: any
  inventoryId: string
  open: boolean
  onClose: (boolean) => void
}

const EditSpaceLoadImageModal: FunctionComponent<EditSpaceLoadImageModalProps> = ({
  floorMap,
  snackBars,
  inventoryId,
  open,
  onClose,
}) => {
  const [imgUrl, setImageUrl] = useState<string | null>()
  const [imgFile, setImageFile] = useState<File | null>()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleLoadImage = async () => {
    const inventoryExtension = floorMap.getExtension('inventoryData')

    if (imgUrl) {
      try {
        const name = imgUrl.split('/').pop()
        setIsLoading(true)
        const imgExt = imgUrl.split('.').pop()
        await inventoryExtension.addImageURL(inventoryId, name, imgExt, imgUrl)
        snackBars.addAlert({ text: 'Image loaded', variant: 'success' })
        onClose(true)
      } catch (exception) {
        snackBars.addAlert({ text: exception.message, variant: 'error' })
      } finally {
        setIsLoading(false)
      }
    } else if (imgFile) {
      const reader = new FileReader()
      reader.onload = async e => {
        try {
          setIsLoading(true)
          const encodeFile = e.target?.result
            ?.toString()
            .split('base64,')
            .pop()
          const imgExt = imgFile.name.split('.').pop()
          await inventoryExtension.addImageFile(
            inventoryId,
            imgFile.name,
            imgExt,
            encodeFile
          )
          snackBars.addAlert({ text: 'Image loaded', variant: 'success' })
          onClose(true)
        } catch (exception) {
          snackBars.addAlert({ text: exception.message, variant: 'error' })
        } finally {
          setIsLoading(false)
        }
      }
      reader.readAsDataURL(imgFile)
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Content>
        {isLoading ? (
          <Grid style={{ padding: 8, height: '100px' }}>
            <Dimmer active inverted>
              <Loader active inline="centered" />
            </Dimmer>
          </Grid>
        ) : (
          <Grid style={{ padding: 8 }}>
            <Grid.Row>
              <Grid.Column width={8}>
                <Text style={{ fontSize: IMTheme.fontSizes[2] }}>
                  Photo of this space
                </Text>
                <Text
                  style={{
                    fontSize: IMTheme.fontSizes[1],
                    color: IMTheme.colors.darkGray,
                  }}
                >
                  A photo of how this space looks like.
                </Text>
              </Grid.Column>
              <Grid.Column width={8}>
                <DragNDroopFile
                  onDrop={acceptedFiles => {
                    if (acceptedFiles[0]) {
                      setImageFile(acceptedFiles[0])
                      if (imgUrl) {
                        setImageUrl(null)
                      }
                    } else {
                      setImageFile(null)
                    }
                  }}
                  multiple={false}
                  accept=".jpeg, .jpg, .png, .gif, .bmp"
                />
                <Text
                  style={{
                    marginTop: IMTheme.spacing[4],
                    fontSize: IMTheme.fontSizes[1],
                    color: IMTheme.colors.darkGray,
                  }}
                >
                  Alternatively, you can also add using an image url.
                </Text>
                <Grid.Row style={{ marginTop: IMTheme.spacing[4] }}>
                  <Form style={{ width: '100%' }}>
                    <Form.Field
                      id="imageUrl"
                      type="text"
                      placeholder="Image URL"
                      control={Input}
                      onChange={e => {
                        //@ts-ignore
                        setImageUrl(e.target.value || '')
                        if (imgFile) {
                          setImageFile(null)
                        }
                      }}
                    />
                  </Form>
                  <Button
                    basic
                    active={true}
                    disabled={!imgUrl && !imgFile}
                    style={{
                      width: 40,
                      height: 40,
                      marginLeft: IMTheme.spacing[2],
                    }}
                    onClick={handleLoadImage}
                    icon={<CheckIcon />}
                  />
                </Grid.Row>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      </Modal.Content>
    </Modal>
  )
}

export default compose(withFloorMap())(withSnackbar(EditSpaceLoadImageModal))
